import {
  AlertCircle,
  CircleHelp,
  Info,
  Sparkles,
  TriangleAlert,
} from "lucide-react"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { formatCurrency, formatDate } from "@/lib/utils"
import type {
  AgentConversationDetail,
  AgentExchange,
  AgentFacts,
  AgentMessage,
} from "@/types"

const SUGGESTIONS = [
  "How much did I spend last month?",
  "Show income by tag this year",
  "Compare this month with previous months",
]

function cents(value: number | undefined) {
  return formatCurrency((value ?? 0) / 100)
}

function percentage(basisPoints: number) {
  return `${(basisPoints / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`
}

function Facts({ facts }: { facts: AgentFacts }) {
  const hasOverlappingTags = facts.groups?.some((group) => group.overlapping)

  return (
    <div className="mt-3 space-y-3">
      {facts.period && (
        <div className="text-sm">
          <p className="font-medium">{facts.period.label}</p>
          <p className="text-muted-foreground text-xs">
            {formatDate(facts.period.start_date)}–
            {formatDate(facts.period.end_date)}
          </p>
        </div>
      )}

      {typeof facts.value_cents === "number" && (
        <p className="text-2xl font-semibold tabular-nums">
          {cents(facts.value_cents)}
        </p>
      )}

      {facts.periods && facts.periods.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Period</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Change</TableHead>
              <TableHead className="text-right">Percent</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {facts.periods.map((point) => (
              <TableRow
                key={`${point.period.start_date}-${point.period.end_date}`}
              >
                <TableCell>
                  <span className="block">{point.period.label}</span>
                  <span className="text-muted-foreground text-xs">
                    {formatDate(point.period.start_date)}–
                    {formatDate(point.period.end_date)}
                  </span>
                </TableCell>
                <TableCell className="text-right font-medium tabular-nums">
                  {cents(point.value_cents)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {typeof point.delta_cents === "number"
                    ? cents(point.delta_cents)
                    : "—"}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {typeof point.percentage_basis_points === "number"
                    ? percentage(point.percentage_basis_points)
                    : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {facts.groups && facts.groups.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                {facts.groups.some((group) => group.overlapping)
                  ? "Tag"
                  : "Category"}
              </TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {facts.groups.map((group, index) => (
              <TableRow key={`${group.label}-${index}`}>
                <TableCell>{group.label}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {cents(group.amount_cents)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {hasOverlappingTags && (
        <Alert>
          <Info />
          <AlertTitle>Tag totals overlap</AlertTitle>
          <AlertDescription>
            An entry can have more than one tag, so these rows may not add up to
            the total.
          </AlertDescription>
        </Alert>
      )}

      {facts.warnings?.map((warning, index) => (
        <Alert key={`${warning}-${index}`}>
          <TriangleAlert />
          <AlertTitle>About this result</AlertTitle>
          <AlertDescription>{warning}</AlertDescription>
        </Alert>
      ))}

      {facts.generated_at && (
        <p className="text-muted-foreground text-xs">
          Generated {new Date(facts.generated_at).toLocaleString()}
        </p>
      )}
    </div>
  )
}

function ExchangeState({
  exchange,
  assistant,
  onRetry,
}: {
  exchange: AgentExchange
  assistant?: AgentMessage
  onRetry: (exchange: AgentExchange) => void
}) {
  if (exchange.state === "pending") {
    return (
      <div className="bg-muted flex items-center gap-2 rounded-lg p-3 text-sm">
        <Spinner />
        Reviewing your recorded finances…
      </div>
    )
  }

  if (exchange.state === "failed" || exchange.state === "indeterminate") {
    return (
      <Alert variant="destructive">
        <AlertCircle />
        <AlertTitle>
          {exchange.state === "indeterminate"
            ? "The request status is uncertain"
            : "The answer could not be completed"}
        </AlertTitle>
        <AlertDescription>
          <p>
            {exchange.state === "indeterminate"
              ? "Check your connection, then retry this same request."
              : "Your question is saved. You can safely try it again."}
          </p>
          {exchange.retryable && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onRetry(exchange)}
            >
              Retry
            </Button>
          )}
        </AlertDescription>
      </Alert>
    )
  }

  if (exchange.result_type === "clarification") {
    return (
      <Alert>
        <CircleHelp />
        <AlertTitle>More detail needed</AlertTitle>
        <AlertDescription>{assistant?.content}</AlertDescription>
      </Alert>
    )
  }

  if (exchange.result_type === "unsupported") {
    return (
      <Alert>
        <Info />
        <AlertTitle>Outside this assistant’s scope</AlertTitle>
        <AlertDescription>{assistant?.content}</AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="bg-muted rounded-lg p-3 text-sm">
      {assistant?.content && (
        <p className="whitespace-pre-wrap">{assistant.content}</p>
      )}
      {exchange.scoped_refusal && (
        <Alert className="mt-3">
          <Info />
          <AlertTitle>About the rest of your request</AlertTitle>
          <AlertDescription>{exchange.scoped_refusal}</AlertDescription>
        </Alert>
      )}
      {exchange.result_type === "empty" && (
        <p className="text-muted-foreground mt-2">
          No recorded activity matched that question.
        </p>
      )}
      <Facts facts={exchange.facts} />
    </div>
  )
}

export function MessageList({
  detail,
  loading,
  onSuggestion,
  onRetry,
}: {
  detail: AgentConversationDetail | null
  loading: boolean
  onSuggestion: (prompt: string) => void
  onRetry: (exchange: AgentExchange) => void
}) {
  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center" role="status">
        <Spinner className="size-5" />
        <span className="sr-only">Loading conversation</span>
      </div>
    )
  }

  if (!detail) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center p-6 text-center">
        <span className="bg-primary/10 text-primary mb-4 rounded-full p-3">
          <Sparkles className="size-6" />
        </span>
        <h3 className="font-semibold">Ask about your finances</h3>
        <p className="text-muted-foreground mt-1 max-w-sm text-sm">
          Explore income, expenses, categories, tags, and comparisons using your
          recorded data.
        </p>
        <div className="mt-5 flex max-w-md flex-wrap justify-center gap-2">
          {SUGGESTIONS.map((suggestion) => (
            <Button
              key={suggestion}
              type="button"
              variant="outline"
              size="sm"
              className="h-auto whitespace-normal"
              onClick={() => onSuggestion(suggestion)}
            >
              {suggestion}
            </Button>
          ))}
        </div>
      </div>
    )
  }

  const messagesByExchange = new Map<number, AgentMessage[]>()
  detail.messages.forEach((message) => {
    const messages = messagesByExchange.get(message.exchange_id) ?? []
    messages.push(message)
    messagesByExchange.set(message.exchange_id, messages)
  })

  return (
    <div className="flex-1 space-y-4 overflow-y-auto p-4" aria-live="polite">
      {detail.exchanges.map((exchange) => {
        const messages = messagesByExchange.get(exchange.id) ?? []
        const userMessage = messages.find((message) => message.role === "user")
        const assistant = messages.find(
          (message) => message.role === "assistant",
        )

        return (
          <section key={exchange.id} className="space-y-3">
            {userMessage && (
              <div className="bg-primary text-primary-foreground ml-auto max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap">
                {userMessage.content}
              </div>
            )}
            <ExchangeState
              exchange={exchange}
              assistant={assistant}
              onRetry={onRetry}
            />
          </section>
        )
      })}
    </div>
  )
}
